"use client"

/**
 * Prototype states for the student My Exams dashboard — live band count and
 * whether the hub table shows rows. Reviewers switch from the profile menu.
 */

import * as React from "react"
import { usePersistedState } from "@exxatdesignux/ui/lib/persisted-state"

export const STUDENT_EXAM_DASHBOARD_STATE_KEY =
  "shell:student-exam-dashboard-state:v1"

export type StudentExamLiveBandCount = 0 | 1 | 2 | 3

export type StudentExamTableState = "populated" | "empty"

export interface StudentExamDashboardState {
  liveBandCount: StudentExamLiveBandCount
  table: StudentExamTableState
}

export const DEFAULT_STUDENT_EXAM_DASHBOARD_STATE: StudentExamDashboardState = {
  liveBandCount: 2,
  table: "populated",
}

export const STUDENT_EXAM_LIVE_BAND_OPTIONS: readonly {
  value: StudentExamLiveBandCount
  label: string
}[] = [
  { value: 2, label: "2 live exams" },
  { value: 0, label: "No live exams" },
  { value: 1, label: "1 live exam" },
  { value: 3, label: "3 live exams" },
]

export const STUDENT_EXAM_TABLE_OPTIONS: readonly {
  value: StudentExamTableState
  label: string
}[] = [
  { value: "populated", label: "Table with data" },
  { value: "empty", label: "Empty table" },
]

function normalizeLiveBandCount(value: unknown): StudentExamLiveBandCount {
  const n = Number(value)
  if (n === 0 || n === 1 || n === 2 || n === 3) return n
  return DEFAULT_STUDENT_EXAM_DASHBOARD_STATE.liveBandCount
}

function normalizeTableState(value: unknown): StudentExamTableState {
  return value === "empty" ? "empty" : "populated"
}

function normalizeState(value: unknown): StudentExamDashboardState {
  if (!value || typeof value !== "object") {
    return DEFAULT_STUDENT_EXAM_DASHBOARD_STATE
  }
  const record = value as Partial<StudentExamDashboardState>
  return {
    liveBandCount: normalizeLiveBandCount(record.liveBandCount),
    table: normalizeTableState(record.table),
  }
}

interface StudentExamDashboardStateContextValue {
  state: StudentExamDashboardState
  setLiveBandCount: (count: StudentExamLiveBandCount) => void
  setTableState: (table: StudentExamTableState) => void
}

const StudentExamDashboardStateContext =
  React.createContext<StudentExamDashboardStateContextValue>({
    state: DEFAULT_STUDENT_EXAM_DASHBOARD_STATE,
    setLiveBandCount: () => {},
    setTableState: () => {},
  })

export function useStudentExamDashboardState() {
  return React.useContext(StudentExamDashboardStateContext)
}

export function studentExamDashboardStateSummary(
  state: StudentExamDashboardState,
): string {
  const live =
    STUDENT_EXAM_LIVE_BAND_OPTIONS.find(
      option => option.value === state.liveBandCount,
    )?.label ?? `${state.liveBandCount} live`
  const table =
    STUDENT_EXAM_TABLE_OPTIONS.find(option => option.value === state.table)
      ?.label ?? state.table
  return `${live} · ${table}`
}

export function StudentExamDashboardStateProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [stored, setStored] = usePersistedState<StudentExamDashboardState>(
    STUDENT_EXAM_DASHBOARD_STATE_KEY,
    DEFAULT_STUDENT_EXAM_DASHBOARD_STATE,
    { debounceMs: 0 },
  )

  const state = normalizeState(stored)

  const setLiveBandCount = React.useCallback(
    (liveBandCount: StudentExamLiveBandCount) => {
      setStored(prev => ({ ...normalizeState(prev), liveBandCount }))
    },
    [setStored],
  )

  const setTableState = React.useCallback(
    (table: StudentExamTableState) => {
      setStored(prev => ({ ...normalizeState(prev), table }))
    },
    [setStored],
  )

  const value = React.useMemo(
    () => ({ state, setLiveBandCount, setTableState }),
    [state, setLiveBandCount, setTableState],
  )

  return (
    <StudentExamDashboardStateContext.Provider value={value}>
      {children}
    </StudentExamDashboardStateContext.Provider>
  )
}
